'use client';

import { Controller, FormProvider } from 'react-hook-form';
import { Typography, Button, Input } from '@/app/common';
import type { NewsletterBlock } from './newsletter.types';
import { processHeading } from '@workspace/lib/heading-utils';
import { cn, getBackgroundClass } from '@workspace/lib';
import { NEWSLETTER_MESSAGES } from '@workspace/constants/newsletter.constant';
import { useNewsletter } from './useNewsletter';

export function Newsletter({ block }: { block: NewsletterBlock }) {
  const {
    heading,
    description,
    placeholder = 'Enter your email address',
    buttonText = 'Subscribe',
    backgroundColor,
  } = block || ({} as NewsletterBlock);

  const { form, onSubmit, handleInvalidSubmit, showSuccess, loading } = useNewsletter();
  const { control, handleSubmit, formState } = form;
  const { errors } = formState;

  const backgroundClass = getBackgroundClass(backgroundColor);
  const headingText = processHeading(heading).text;

  return (
    <div className={cn(backgroundClass)}>
      <section className="porta-container py-xl px-6 md:py-4xl md:px-4xl">
        <div>
          {showSuccess ? (
            <div className="flex flex-col items-start justify-center gap-3xs md:gap-sm md:w-125.25 md:h-44.5">
              <Typography variant="h3" className="text-lg tracking-tight">
                You&apos;re subscribed to the Porta Newsletter!
              </Typography>
              <Typography variant="bodyMd" className="text-secondary-600">
                Check your inbox for a confirmation email.
              </Typography>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row items-center gap-lg md:gap-4xl">
              {/* Left side */}
              <div className="w-full md:max-w-125">
                <Typography variant="h3" className="font-bold  mb-sm">
                  {headingText}
                </Typography>
                <Typography variant="bodyMd" className="max-w-prose text-secondary-500">
                  {description}
                </Typography>
              </div>

              {/* Right side form */}
              <FormProvider {...form}>
                <form onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)} noValidate className="w-full md:w-135">
                  <Controller
                    control={control}
                    name="honeypot"
                    render={({ field }) => (
                      <input type="text" tabIndex={-1} autoComplete="off" style={{ display: 'none' }} {...field} />
                    )}
                  />
                  <div className="flex flex-col md:flex-row md:items-start gap-sm md:gap-xs">
                    <div className="flex-1">
                      <label
                        htmlFor="newsletter-email"
                        className="block text-base font-semibold mb-xs text-secondary-400"
                      >
                        Email
                      </label>
                      <Controller
                        control={control}
                        name="email"
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder={placeholder}
                            maxLength={255}
                            variant="field"
                            status={errors.email ? 'error' : 'default'}
                            className="font-body w-full max-w-none h-11 py-xs px-button border rounded-sm bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        )}
                      />
                      <div className="md:min-h-3.75 md:mt-xs">
                        {errors.email && (
                          <Typography variant="bodyXs" className="mt-xs text-error " role="alert">
                            {errors.email.message || NEWSLETTER_MESSAGES.ERROR}
                          </Typography>
                        )}
                      </div>
                    </div>

                    <Button
                      variant="primary"
                      type="submit"
                      isDisabled={loading}
                      aria-disabled={loading}
                      className="h-11 w-full md:w-45 px-md rounded-sm font-semibold md:mt-8"
                    >
                      <span className="block w-full text-center text-white font-heading text-base font-semibold leading-5">
                        {loading ? 'Sending...' : buttonText}
                      </span>
                    </Button>
                  </div>
                </form>
              </FormProvider>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
